import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  ConfigurationTemplate,
  Rule,
  RuleColumn,
  ColumnMapping,
  SectionType,
  SectionConfig,
  ConfigurationSection,
  Label,
  RuleCondition,
  UIInstrumentType,
  ALL_UI_INSTRUMENT_TYPES,
  SectionId,
  getRulesSectionId,
  FeeTypeCategory,
  FEE_TYPE_CATEGORIES,
  BuySellCategory,
  BUY_SELL_CATEGORIES,
  DepositWithdrawCategory,
  DEPOSIT_WITHDRAW_CATEGORIES,
  FxSpotCategory,
  FX_SPOT_CATEGORIES,
  SettlementAccountCategory,
  SETTLEMENT_ACCOUNT_CATEGORIES,
  DeductionRebateCategory,
  DEDUCTION_REBATE_CATEGORIES,
  CategoryConfig,
  SignDetectionConfig,
} from '../components/DataFeed/Configuration/types';
import { getSectionsForInstrumentType } from '../components/DataFeed/Configuration/configs/sectionConfigs';
import { Currency } from '../types/generatedTypes';
import { DEFAULT_CURRENCIES } from '../components/DataFeed/Configuration/components/ConfigurationSections/CurrencySection/currencyData';
import { ValidationResult } from '../components/DataFeed/Configuration/validation/types';
import { validateSection } from '../components/DataFeed/Configuration/validation/validationUtils';

// ==================== Helper Functions ====================

/**
 * Initialize empty config based on section type
 */
export const initializeConfig = (
  type: SectionType,
  sectionId?: string
): SectionConfig => {
  switch (type) {
    case SectionType.COLUMN_MAPPING:
      // INSTRUMENT_IDENTIFIERS section uses inline rules with one empty column each
      if (sectionId === SectionId.INSTRUMENT_IDENTIFIERS) {
        return {
          rules: [
            {
              id: `rule-default-${Date.now()}-${Math.random()}`,
              name: 'Default Rule',
              columns: [
                {
                  id: `col-${Date.now()}-${Math.random()}`,
                  sourceColumn: '',
                  labels: [],
                },
              ],
              conditions: [],
            },
          ],
          // COMMENTED OUT: Holdings lookup functionality disabled
          // holdingsLookup: {
          //   enabled: false,
          //   symbolColumn: null
          // }
        };
      }
      return { columnMappings: [] };
    case SectionType.RULES:
      return { rules: [] };
    case SectionType.SINGLE_COLUMN:
      return {
        columnMappings: [
          {
            id: `col-initial-${Date.now()}-${Math.random()}`,
            sourceColumn: '',
          },
        ],
      };
    case SectionType.SINGLE_COLUMN_WITH_DATE:
      return {
        columnMappings: [
          {
            id: `col-initial-${Date.now()}-${Math.random()}`,
            sourceColumn: '',
          },
        ],
        dateFormat: '',
      };
    case SectionType.SINGLE_COLUMN_WITH_LABELS:
      return {
        columnMappings: [
          {
            id: `col-initial-${Date.now()}-${Math.random()}`,
            sourceColumn: '',
          },
        ],
        labels: [],
      };
    case SectionType.MATH_OPERATIONS:
      return {
        mathOperation: {
          formula: '',
          variables: {},
        },
      };
    case SectionType.CURRENCY:
      return {
        columnMappings: [
          {
            id: `col-initial-${Date.now()}-${Math.random()}`,
            sourceColumn: '',
          },
        ],
        currencyMappings: DEFAULT_CURRENCIES.map(currency => ({
          currency: currency,
          variations: [],
        })),
      };
    case SectionType.MULTI_COLUMN_RULES_WITH_CATEGORIES: {
      // Determine which categories to use based on sectionId
      let categories: readonly CategoryConfig[];
      if (sectionId === SectionId.BUY_SELL) {
        categories = BUY_SELL_CATEGORIES;
      } else if (sectionId === SectionId.FEE_TYPE) {
        categories = FEE_TYPE_CATEGORIES;
      } else if (sectionId === SectionId.DEPOSIT_WITHDRAW_TYPE) {
        categories = DEPOSIT_WITHDRAW_CATEGORIES;
      } else if (sectionId === SectionId.FX_SPOT_TYPE) {
        categories = FX_SPOT_CATEGORIES;
      } else if (sectionId === SectionId.SETTLEMENT_ACCOUNT) {
        categories = SETTLEMENT_ACCOUNT_CATEGORIES;
      } else if (sectionId === SectionId.DEDUCTION_REBATE) {
        categories = DEDUCTION_REBATE_CATEGORIES;
      } else {
        // This should never happen - make it visible
        console.error(
          `⚠️ Unknown section ID for MULTI_COLUMN_RULES_WITH_CATEGORIES: ${sectionId}`
        );
        categories = [];
      }

      // Sections that support sign detection
      const sectionsWithSignDetection = [
        SectionId.BUY_SELL,
        SectionId.DEPOSIT_WITHDRAW_TYPE,
        SectionId.FX_SPOT_TYPE,
        SectionId.DEDUCTION_REBATE,
      ];

      // Base config with category rules
      const baseConfig = {
        categoryRules: categories.map(category => ({
          categoryId: category.id,
          categoryName: category.title,
          rules: [
            {
              id: `rule-default-${category.id}-${Date.now()}`,
              name: 'Rule 1',
              columns: [
                {
                  id: `col-${Date.now()}-${Math.random()}`,
                  sourceColumn: '',
                  labels: [],
                },
              ],
              conditions: [],
            },
          ],
        })),
      };

      // Add signDetection only for specific sections
      if (sectionsWithSignDetection.includes(sectionId as SectionId)) {
        return {
          ...baseConfig,
          signDetection: {
            sourceColumn: '',
            negativeCategory: '',
            positiveCategory: '',
          },
        };
      }

      return baseConfig;
    }
    default:
      return {};
  }
};

/**
 * Generate a composite key for validation state
 * Scoped by template, instrument type, and section
 */
const getValidationKey = (
  templateId: string,
  instrumentType: string,
  sectionId: string
): string => {
  return `${templateId}_${instrumentType}_${sectionId}`;
};

/**
 * Generic helper to update items in an array by id
 */
const updateInArray = <T extends { id: string }>(
  array: T[],
  itemId: string,
  updates: Partial<T>
): T[] => {
  return array.map(item =>
    item.id === itemId ? { ...item, ...updates } : item
  );
};

/**
 * Generic helper to delete items from an array by id
 */
const deleteFromArray = <T extends { id: string }>(
  array: T[],
  itemId: string
): T[] => {
  return array.filter(item => item.id !== itemId);
};

/**
 * Generic helper to update nested template sections
 * Handles the repetitive template -> instrumentConfig -> section navigation
 */
const updateNestedTemplate = (
  templates: ConfigurationTemplate[],
  templateId: string,
  instrumentType: UIInstrumentType,
  sectionId: string,
  updater: (sectionConfig: SectionConfig) => SectionConfig
): ConfigurationTemplate[] => {
  return templates.map(template => {
    if (template.id !== templateId) return template;

    return {
      ...template,
      instrumentConfigs: template.instrumentConfigs.map(config => {
        if (config.instrumentType !== instrumentType) return config;

        return {
          ...config,
          sections: config.sections.map(section => {
            if (section.id !== sectionId) return section;

            return {
              ...section,
              config: updater(section.config),
            };
          }),
        };
      }),
    };
  });
};

/**
 * Generic helper to update a rule's columns and synchronize conditions array.
 * Ensures conditions array is always n-1 for n columns.
 */
const updateRuleColumns = (
  rule: Rule,
  updateFn: (columns: RuleColumn[]) => RuleColumn[]
): Rule => {
  const newColumns = updateFn(rule.columns);
  const newConditions = Array.from(
    { length: Math.max(0, newColumns.length - 1) },
    (_, i) => rule.conditions?.[i] || RuleCondition.OR
  );
  return { ...rule, columns: newColumns, conditions: newConditions };
};

/**
 * Generic helper to update a section and automatically validate it.
 * Eliminates duplicate validation logic across all section update actions.
 *
 * @param state - Current store state
 * @param templateId - Target template ID
 * @param instrumentType - Target instrument type
 * @param sectionId - Target section ID
 * @param configUpdater - Function to update the section config
 * @returns New state with updated templates and validation
 */
const updateSectionWithValidation = (
  state: ConfigurationState,
  templateId: string,
  instrumentType: UIInstrumentType,
  sectionId: string,
  configUpdater: (config: SectionConfig) => SectionConfig
) => {
  // 1. Update the config
  const updatedTemplates = updateNestedTemplate(
    state.templates,
    templateId,
    instrumentType,
    sectionId,
    configUpdater
  );

  // 2. Find the updated section
  const template = updatedTemplates.find(t => t.id === templateId);
  const instrumentConfig = template?.instrumentConfigs.find(
    c => c.instrumentType === instrumentType
  );
  const section = instrumentConfig?.sections.find(s => s.id === sectionId);

  // 3. Validate the section
  const validationResult = section
    ? validateSection(section)
    : { isValid: true, errors: [], warnings: [] };
  const key = getValidationKey(templateId, instrumentType, sectionId);

  // 4. Return new state
  return {
    templates: updatedTemplates,
    validationState: {
      ...state.validationState,
      [key]: validationResult,
    },
  };
};

/**
 * Ensures a template has all instrument types defined in ALL_UI_INSTRUMENT_TYPES.
 * For any missing types, creates a proper InstrumentConfig with correct sections.
 */
const ensureAllInstrumentTypes = (
  template: ConfigurationTemplate
): ConfigurationTemplate => {
  const existingTypes = new Set(
    template.instrumentConfigs.map(ic => ic.instrumentType)
  );

  const missingTypes = ALL_UI_INSTRUMENT_TYPES.filter(
    type => !existingTypes.has(type)
  );

  if (missingTypes.length === 0) {
    return template; // No migration needed
  }

  // Create InstrumentConfigs for missing types using same logic as createDefaultTemplate
  const newInstrumentConfigs = missingTypes.map(instrumentType => {
    const sectionConfigs = getSectionsForInstrumentType(instrumentType);

    const sections: ConfigurationSection[] = sectionConfigs.map(
      sectionConfig => ({
        id: sectionConfig.id,
        title: sectionConfig.title,
        type: sectionConfig.type,
        config: initializeConfig(sectionConfig.type, sectionConfig.id),
      })
    );

    return {
      instrumentType,
      sections,
    };
  });

  return {
    ...template,
    instrumentConfigs: [...template.instrumentConfigs, ...newInstrumentConfigs],
  };
};

/**
 * Ensures all sections exist for each instrument type in a template.
 * For any missing sections, creates them with proper default configuration.
 */
const ensureAllSectionsForInstrumentType = (
  template: ConfigurationTemplate
): ConfigurationTemplate => {
  return {
    ...template,
    instrumentConfigs: template.instrumentConfigs.map(instrumentConfig => {
      const requiredSections = getSectionsForInstrumentType(instrumentConfig.instrumentType);
      const requiredById = new Map(requiredSections.map(sc => [sc.id, sc]));
      const existingSectionIds = new Set(instrumentConfig.sections.map(s => s.id));

      // Fix sections whose type has changed (e.g. QUANTITY: SINGLE_COLUMN → MATH_OPERATIONS)
      const updatedSections = instrumentConfig.sections.map(section => {
        const required = requiredById.get(section.id);
        if (required && required.type !== section.type) {
          return {
            id: section.id,
            title: required.title,
            type: required.type,
            config: initializeConfig(required.type, section.id),
          };
        }
        return section;
      });

      // Add truly missing sections
      const missingSections = requiredSections.filter(sc => !existingSectionIds.has(sc.id));
      const newSections: ConfigurationSection[] = missingSections.map(sc => ({
        id: sc.id,
        title: sc.title,
        type: sc.type,
        config: initializeConfig(sc.type, sc.id),
      }));

      return {
        ...instrumentConfig,
        sections: [...updatedSections, ...newSections],
      };
    }),
  };
};

/**
 * Creates a default template with all instrument types
 */
export const createDefaultTemplate = (name: string): ConfigurationTemplate => {
  const templateId = `template-${Date.now()}`;

  const instrumentConfigs = ALL_UI_INSTRUMENT_TYPES.map(instrumentType => {
    const sectionConfigs = getSectionsForInstrumentType(instrumentType);

    // Create visible sections - INSTRUMENT_IDENTIFIERS now has inline rules, no separate section needed
    const sections: ConfigurationSection[] = sectionConfigs.map(
      sectionConfig => ({
        id: sectionConfig.id,
        title: sectionConfig.title,
        type: sectionConfig.type,
        config: initializeConfig(sectionConfig.type, sectionConfig.id),
      })
    );

    // Note: INSTRUMENT_IDENTIFIERS now uses inline rules (see initializeConfig)
    // No need to auto-create separate RULES sections anymore

    return {
      instrumentType,
      sections,
    };
  });

  return {
    id: templateId,
    name,
    instrumentConfigs,
  };
};

// ==================== Store Interface ====================

interface ConfigurationState {
  // State
  templates: ConfigurationTemplate[];
  activeTemplateId: string | null;
  activeInstrumentType: UIInstrumentType | null;
  availableHeaders: string[];
  columnUniqueValues: Record<string, string[]>;
  isSaving: boolean;
  lastSavedAt: string | null;
  validationState: Record<string, ValidationResult>;

  // Template Management Actions
  setActiveTemplate: (id: string) => void;
  setActiveInstrumentType: (type: UIInstrumentType) => void;
  setAvailableHeaders: (headers: string[]) => void;
  setColumnUniqueValues: (values: Record<string, string[]>) => void;
  addTemplate: (template: ConfigurationTemplate) => void;
  updateTemplate: (id: string, updates: Partial<ConfigurationTemplate>) => void;
  deleteTemplate: (id: string) => void;
  setTemplates: (templates: ConfigurationTemplate[]) => void;

  // Rule Management Actions
  addRule: (
    templateId: string,
    instrumentType: UIInstrumentType,
    sectionId: SectionId,
    rule: Rule
  ) => void;
  updateRule: (
    templateId: string,
    instrumentType: UIInstrumentType,
    sectionId: SectionId,
    ruleId: string,
    updates: Partial<Rule>
  ) => void;
  deleteRule: (
    templateId: string,
    instrumentType: UIInstrumentType,
    sectionId: SectionId,
    ruleId: string
  ) => void;

  // Rule Column Management Actions
  addColumnToRule: (
    templateId: string,
    instrumentType: UIInstrumentType,
    sectionId: SectionId,
    ruleId: string,
    column: RuleColumn
  ) => void;
  removeColumnFromRule: (
    templateId: string,
    instrumentType: UIInstrumentType,
    sectionId: SectionId,
    ruleId: string,
    columnId: string
  ) => void;
  updateRuleColumn: (
    templateId: string,
    instrumentType: UIInstrumentType,
    sectionId: SectionId,
    ruleId: string,
    columnId: string,
    updates: Partial<RuleColumn>
  ) => void;
  addLabelToRuleColumn: (
    templateId: string,
    instrumentType: UIInstrumentType,
    sectionId: SectionId,
    ruleId: string,
    columnId: string,
    label: Label
  ) => void;
  removeLabelFromRuleColumn: (
    templateId: string,
    instrumentType: UIInstrumentType,
    sectionId: SectionId,
    ruleId: string,
    columnId: string,
    labelId: string
  ) => void;

  // Column Mapping Actions
  addColumnMapping: (
    templateId: string,
    instrumentType: UIInstrumentType,
    sectionId: string,
    mapping: ColumnMapping
  ) => void;
  updateColumnMapping: (
    templateId: string,
    instrumentType: UIInstrumentType,
    sectionId: string,
    columnId: string,
    updates: Partial<ColumnMapping>
  ) => void;
  deleteColumnMapping: (
    templateId: string,
    instrumentType: UIInstrumentType,
    sectionId: string,
    columnId: string
  ) => void;

  // Category Rule Management Actions
  addCategoryRule: (
    templateId: string,
    instrumentType: UIInstrumentType,
    sectionId: string,
    categoryId:
      | FeeTypeCategory
      | BuySellCategory
      | DepositWithdrawCategory
      | FxSpotCategory
      | SettlementAccountCategory
      | DeductionRebateCategory,
    rule: Rule
  ) => void;
  updateCategoryRule: (
    templateId: string,
    instrumentType: UIInstrumentType,
    sectionId: string,
    categoryId:
      | FeeTypeCategory
      | BuySellCategory
      | DepositWithdrawCategory
      | FxSpotCategory
      | SettlementAccountCategory
      | DeductionRebateCategory,
    ruleId: string,
    updates: Partial<Rule>
  ) => void;
  deleteCategoryRule: (
    templateId: string,
    instrumentType: UIInstrumentType,
    sectionId: string,
    categoryId:
      | FeeTypeCategory
      | BuySellCategory
      | DepositWithdrawCategory
      | FxSpotCategory
      | SettlementAccountCategory
      | DeductionRebateCategory,
    ruleId: string
  ) => void;

  // Category Rule Column Management Actions
  addColumnToCategoryRule: (
    templateId: string,
    instrumentType: UIInstrumentType,
    sectionId: string,
    categoryId:
      | FeeTypeCategory
      | BuySellCategory
      | DepositWithdrawCategory
      | FxSpotCategory
      | SettlementAccountCategory
      | DeductionRebateCategory,
    ruleId: string,
    column: RuleColumn
  ) => void;
  removeColumnFromCategoryRule: (
    templateId: string,
    instrumentType: UIInstrumentType,
    sectionId: string,
    categoryId:
      | FeeTypeCategory
      | BuySellCategory
      | DepositWithdrawCategory
      | FxSpotCategory
      | SettlementAccountCategory
      | DeductionRebateCategory,
    ruleId: string,
    columnId: string
  ) => void;
  updateCategoryRuleColumn: (
    templateId: string,
    instrumentType: UIInstrumentType,
    sectionId: string,
    categoryId:
      | FeeTypeCategory
      | BuySellCategory
      | DepositWithdrawCategory
      | FxSpotCategory
      | SettlementAccountCategory
      | DeductionRebateCategory,
    ruleId: string,
    columnId: string,
    updates: Partial<RuleColumn>
  ) => void;
  addLabelToCategoryRuleColumn: (
    templateId: string,
    instrumentType: UIInstrumentType,
    sectionId: string,
    categoryId:
      | FeeTypeCategory
      | BuySellCategory
      | DepositWithdrawCategory
      | FxSpotCategory
      | SettlementAccountCategory
      | DeductionRebateCategory,
    ruleId: string,
    columnId: string,
    label: Label
  ) => void;
  removeLabelFromCategoryRuleColumn: (
    templateId: string,
    instrumentType: UIInstrumentType,
    sectionId: string,
    categoryId:
      | FeeTypeCategory
      | BuySellCategory
      | DepositWithdrawCategory
      | FxSpotCategory
      | SettlementAccountCategory
      | DeductionRebateCategory,
    ruleId: string,
    columnId: string,
    labelId: string
  ) => void;

  // Section Config Actions
  updateSectionDateFormat: (
    templateId: string,
    instrumentType: UIInstrumentType,
    sectionId: string,
    dateFormat: string
  ) => void;
  updateMathOperation: (
    templateId: string,
    instrumentType: UIInstrumentType,
    sectionId: string,
    mathOperation: { formula: string; variables: { [key: string]: string } }
  ) => void;
  updateCurrencyMappings: (
    templateId: string,
    instrumentType: UIInstrumentType,
    sectionId: string,
    mappings: { currency: Currency; variations: string[] }[]
  ) => void;
  addCurrencyVariation: (
    templateId: string,
    instrumentType: UIInstrumentType,
    sectionId: string,
    currency: Currency,
    variation: string
  ) => void;
  removeCurrencyVariation: (
    templateId: string,
    instrumentType: UIInstrumentType,
    sectionId: string,
    currency: Currency,
    variation: string
  ) => void;
  updateSignDetection: (
    templateId: string,
    instrumentType: UIInstrumentType,
    sectionId: string,
    signDetection: SignDetectionConfig
  ) => void;

  // Auto-save Actions
  setSaving: (isSaving: boolean) => void;
  setLastSavedAt: (timestamp: string | null) => void;

  // COMMENTED OUT: Holdings lookup functionality disabled
  // // Holdings Lookup Actions
  // updateHoldingsLookup: (
  //   templateId: string,
  //   instrumentType: UIInstrumentType,
  //   sectionId: string,
  //   config: { enabled: boolean; symbolColumn: string | null }
  // ) => void;

  // Transfer Labels Actions
  addTransferLabel: (
    templateId: string,
    instrumentType: UIInstrumentType,
    sectionId: string,
    label: Label
  ) => void;
  removeTransferLabel: (
    templateId: string,
    instrumentType: UIInstrumentType,
    sectionId: string,
    labelId: string
  ) => void;

  // Validation Actions
  setValidation: (sectionId: string, result: ValidationResult) => void;
  clearValidation: (sectionId: string) => void;
  getValidationForSection: (sectionId: string) => ValidationResult | null;
  getTabErrorCount: (instrumentType: UIInstrumentType) => number;
}

const useConfigurationStore = create<ConfigurationState>()(
  devtools(
    (set, get) => {
      return {
        // Initial State
        templates: [], // Start empty, load from DB or create default
        activeTemplateId: null,
        activeInstrumentType: null,
        availableHeaders: [],
        columnUniqueValues: {},
        isSaving: false,
        lastSavedAt: null,
        validationState: {},

        // Template Selection Actions
        setActiveTemplate: (id: string) => {
          const templates = get().templates;
          const foundTemplate = templates.find(t => t.id === id);

          if (!foundTemplate) {
            throw new Error(`⚠️ Template with id "${id}" not found`);
          }

          // Apply defensive migrations to ensure all instrument types and sections exist
          let template = ensureAllInstrumentTypes(foundTemplate);
          template = ensureAllSectionsForInstrumentType(template);

          // Update templates array with migrated version
          const updatedTemplates = templates.map(t =>
            t.id === id ? template : t
          );

          // Auto-select Equities when template is selected, fallback to first available
          const firstInstrumentType =
            template.instrumentConfigs.find(
              ic => ic.instrumentType === 'Equities'
            )?.instrumentType ||
            template.instrumentConfigs[0]?.instrumentType ||
            null;

          // Create fresh validation state for ALL instrument types in this template
          const newValidationState: Record<string, ValidationResult> = {};
          template.instrumentConfigs.forEach(instrumentConfig => {
            instrumentConfig.sections.forEach(section => {
              const key = getValidationKey(
                template.id,
                instrumentConfig.instrumentType,
                section.id
              );
              newValidationState[key] = validateSection(section);
            });
          });

          set({
            templates: updatedTemplates,
            activeTemplateId: id,
            activeInstrumentType: firstInstrumentType,
            validationState: newValidationState, // Replace entire state, don't merge
          });
        },

        setActiveInstrumentType: (type: UIInstrumentType) => {
          const { activeTemplateId, templates } = get();

          if (!activeTemplateId) {
            throw new Error('⚠️ No active template selected');
          }

          const template = templates.find(t => t.id === activeTemplateId);
          const instrumentConfig = template?.instrumentConfigs.find(
            ic => ic.instrumentType === type
          );

          if (!instrumentConfig) {
            throw new Error(
              `⚠️ Instrument type "${type}" not found in active template`
            );
          }

          set({ activeInstrumentType: type });
        },

        setAvailableHeaders: (headers: string[]) => {
          set({ availableHeaders: headers });
        },

        setColumnUniqueValues: (values: Record<string, string[]>) => {
          set({ columnUniqueValues: values });
        },

        // Template CRUD Actions
        addTemplate: (template: ConfigurationTemplate) => {
          set(state => ({
            templates: [...state.templates, template],
          }));
        },

        updateTemplate: (
          id: string,
          updates: Partial<ConfigurationTemplate>
        ) => {
          set(state => ({
            templates: state.templates.map(template =>
              template.id === id ? { ...template, ...updates } : template
            ),
          }));
        },

        deleteTemplate: (id: string) => {
          const { activeTemplateId, templates } = get();

          // Filter out the deleted template
          const remainingTemplates = templates.filter(t => t.id !== id);

          // Determine new active template
          let newActiveTemplateId = activeTemplateId;
          if (activeTemplateId === id) {
            // If we're deleting the active template
            if (remainingTemplates.length > 0) {
              // Select the first remaining template
              newActiveTemplateId = remainingTemplates[0].id;
            } else {
              // No templates left - clear everything
              newActiveTemplateId = null;
            }
          }

          // Update state
          set({
            templates: remainingTemplates,
            activeTemplateId: newActiveTemplateId,
            // Clear instrument type and validation if no templates remain
            ...(remainingTemplates.length === 0 && {
              activeInstrumentType: null,
              validationState: {},
            }),
          });

          // If we selected a new active template, trigger its full setup
          // This ensures migrations, validation, and instrument type selection happen
          if (newActiveTemplateId && newActiveTemplateId !== activeTemplateId) {
            get().setActiveTemplate(newActiveTemplateId);
          }
        },

        setTemplates: (templates: ConfigurationTemplate[]) => {
          // Apply migrations to ensure all templates have all instrument types and sections
          let migratedTemplates = templates.map(ensureAllInstrumentTypes);
          migratedTemplates = migratedTemplates.map(ensureAllSectionsForInstrumentType);

          // If we have templates, validate all sections in the first template
          if (migratedTemplates.length > 0) {
            const firstTemplate = migratedTemplates[0];
            const newValidationState: Record<string, ValidationResult> = {};

            firstTemplate.instrumentConfigs.forEach(instrumentConfig => {
              instrumentConfig.sections.forEach(section => {
                const key = getValidationKey(
                  firstTemplate.id,
                  instrumentConfig.instrumentType,
                  section.id
                );
                newValidationState[key] = validateSection(section);
              });
            });

            set({
              templates: migratedTemplates,
              activeTemplateId: firstTemplate.id,
              activeInstrumentType: 'Equities',
              validationState: newValidationState,
            });
          } else {
            set({
              templates: migratedTemplates,
              activeTemplateId: null,
              activeInstrumentType: null,
              validationState: {},
            });
          }
        },

        // Rule Management Actions
        addRule: (
          templateId: string,
          instrumentType: UIInstrumentType,
          sectionId: SectionId,
          rule: Rule
        ) => {
          // INSTRUMENT_IDENTIFIERS uses inline rules, others use separate section
          const targetSectionId =
            sectionId === SectionId.INSTRUMENT_IDENTIFIERS
              ? sectionId
              : getRulesSectionId(sectionId);

          set(state =>
            updateSectionWithValidation(
              state,
              templateId,
              instrumentType,
              targetSectionId,
              config => ({
                ...config,
                rules: [...(config.rules || []), rule],
              })
            )
          );
        },

        updateRule: (
          templateId: string,
          instrumentType: UIInstrumentType,
          sectionId: SectionId,
          ruleId: string,
          updates: Partial<Rule>
        ) => {
          // INSTRUMENT_IDENTIFIERS uses inline rules, others use separate section
          const targetSectionId =
            sectionId === SectionId.INSTRUMENT_IDENTIFIERS
              ? sectionId
              : getRulesSectionId(sectionId);

          set(state =>
            updateSectionWithValidation(
              state,
              templateId,
              instrumentType,
              targetSectionId,
              config => ({
                ...config,
                rules: updateInArray(config.rules || [], ruleId, updates),
              })
            )
          );
        },

        deleteRule: (
          templateId: string,
          instrumentType: UIInstrumentType,
          sectionId: SectionId,
          ruleId: string
        ) => {
          // INSTRUMENT_IDENTIFIERS uses inline rules, others use separate section
          const targetSectionId =
            sectionId === SectionId.INSTRUMENT_IDENTIFIERS
              ? sectionId
              : getRulesSectionId(sectionId);

          set(state =>
            updateSectionWithValidation(
              state,
              templateId,
              instrumentType,
              targetSectionId,
              config => ({
                ...config,
                rules: deleteFromArray(config.rules || [], ruleId),
              })
            )
          );
        },

        // Rule Column Management Actions
        addColumnToRule: (
          templateId: string,
          instrumentType: UIInstrumentType,
          sectionId: SectionId,
          ruleId: string,
          column: RuleColumn
        ) => {
          const targetSectionId =
            sectionId === SectionId.INSTRUMENT_IDENTIFIERS
              ? sectionId
              : getRulesSectionId(sectionId);

          set(state =>
            updateSectionWithValidation(
              state,
              templateId,
              instrumentType,
              targetSectionId,
              config => ({
                ...config,
                rules: (config.rules || []).map(rule =>
                  rule.id === ruleId
                    ? updateRuleColumns(rule, columns => [...columns, column])
                    : rule
                ),
              })
            )
          );
        },

        removeColumnFromRule: (
          templateId: string,
          instrumentType: UIInstrumentType,
          sectionId: SectionId,
          ruleId: string,
          columnId: string
        ) => {
          const targetSectionId =
            sectionId === SectionId.INSTRUMENT_IDENTIFIERS
              ? sectionId
              : getRulesSectionId(sectionId);

          set(state =>
            updateSectionWithValidation(
              state,
              templateId,
              instrumentType,
              targetSectionId,
              config => ({
                ...config,
                rules: (config.rules || []).map(rule =>
                  rule.id === ruleId
                    ? updateRuleColumns(rule, columns =>
                      columns.filter(col => col.id !== columnId)
                    )
                    : rule
                ),
              })
            )
          );
        },

        updateRuleColumn: (
          templateId: string,
          instrumentType: UIInstrumentType,
          sectionId: SectionId,
          ruleId: string,
          columnId: string,
          updates: Partial<RuleColumn>
        ) => {
          const targetSectionId =
            sectionId === SectionId.INSTRUMENT_IDENTIFIERS
              ? sectionId
              : getRulesSectionId(sectionId);

          set(state =>
            updateSectionWithValidation(
              state,
              templateId,
              instrumentType,
              targetSectionId,
              config => ({
                ...config,
                rules: (config.rules || []).map(rule =>
                  rule.id === ruleId
                    ? updateRuleColumns(rule, columns =>
                      columns.map(col =>
                        col.id === columnId ? { ...col, ...updates } : col
                      )
                    )
                    : rule
                ),
              })
            )
          );
        },

        // Add a label to a specific column in a rule
        addLabelToRuleColumn: (
          templateId: string,
          instrumentType: UIInstrumentType,
          sectionId: SectionId,
          ruleId: string,
          columnId: string,
          label: Label
        ) => {
          const targetSectionId =
            sectionId === SectionId.INSTRUMENT_IDENTIFIERS
              ? sectionId
              : getRulesSectionId(sectionId);

          set(state =>
            updateSectionWithValidation(
              state,
              templateId,
              instrumentType,
              targetSectionId,
              config => ({
                ...config,
                rules: (config.rules || []).map(rule =>
                  rule.id === ruleId
                    ? {
                      ...rule,
                      columns: rule.columns.map(col =>
                        col.id === columnId
                          ? {
                            ...col,
                            labels: [...col.labels, label],
                          }
                          : col
                      ),
                    }
                    : rule
                ),
              })
            )
          );
        },

        // Remove a label from a specific column in a rule
        removeLabelFromRuleColumn: (
          templateId: string,
          instrumentType: UIInstrumentType,
          sectionId: SectionId,
          ruleId: string,
          columnId: string,
          labelId: string
        ) => {
          const targetSectionId =
            sectionId === SectionId.INSTRUMENT_IDENTIFIERS
              ? sectionId
              : getRulesSectionId(sectionId);

          set(state =>
            updateSectionWithValidation(
              state,
              templateId,
              instrumentType,
              targetSectionId,
              config => ({
                ...config,
                rules: (config.rules || []).map(rule =>
                  rule.id === ruleId
                    ? {
                      ...rule,
                      columns: rule.columns.map(col =>
                        col.id === columnId
                          ? {
                            ...col,
                            labels: col.labels.filter(label => label.id !== labelId), // Reads CURRENT col.labels
                          }
                          : col
                      ),
                    }
                    : rule
                ),
              })
            )
          );
        },

        // Category Rule Management Actions
        addCategoryRule: (
          templateId: string,
          instrumentType: UIInstrumentType,
          sectionId: string,
          categoryId:
            | FeeTypeCategory
            | BuySellCategory
            | DepositWithdrawCategory
            | FxSpotCategory
            | SettlementAccountCategory
            | DeductionRebateCategory,
          rule: Rule
        ) => {
          set(state =>
            updateSectionWithValidation(
              state,
              templateId,
              instrumentType,
              sectionId,
              config => ({
                ...config,
                categoryRules: (config.categoryRules || []).map(
                  categorySection =>
                    categorySection.categoryId === categoryId
                      ? {
                        ...categorySection,
                        rules: [...categorySection.rules, rule],
                      }
                      : categorySection
                ),
              })
            )
          );
        },

        updateCategoryRule: (
          templateId: string,
          instrumentType: UIInstrumentType,
          sectionId: string,
          categoryId:
            | FeeTypeCategory
            | BuySellCategory
            | DepositWithdrawCategory
            | FxSpotCategory
            | SettlementAccountCategory
            | DeductionRebateCategory,
          ruleId: string,
          updates: Partial<Rule>
        ) => {
          set(state =>
            updateSectionWithValidation(
              state,
              templateId,
              instrumentType,
              sectionId,
              config => ({
                ...config,
                categoryRules: (config.categoryRules || []).map(
                  categorySection =>
                    categorySection.categoryId === categoryId
                      ? {
                        ...categorySection,
                        rules: updateInArray(
                          categorySection.rules,
                          ruleId,
                          updates
                        ),
                      }
                      : categorySection
                ),
              })
            )
          );
        },

        deleteCategoryRule: (
          templateId: string,
          instrumentType: UIInstrumentType,
          sectionId: string,
          categoryId:
            | FeeTypeCategory
            | BuySellCategory
            | DepositWithdrawCategory
            | FxSpotCategory
            | SettlementAccountCategory
            | DeductionRebateCategory,
          ruleId: string
        ) => {
          set(state =>
            updateSectionWithValidation(
              state,
              templateId,
              instrumentType,
              sectionId,
              config => ({
                ...config,
                categoryRules: (config.categoryRules || []).map(
                  categorySection =>
                    categorySection.categoryId === categoryId
                      ? {
                        ...categorySection,
                        rules: deleteFromArray(categorySection.rules, ruleId),
                      }
                      : categorySection
                ),
              })
            )
          );
        },

        // Category Rule Column Management Actions
        addColumnToCategoryRule: (
          templateId: string,
          instrumentType: UIInstrumentType,
          sectionId: string,
          categoryId:
            | FeeTypeCategory
            | BuySellCategory
            | DepositWithdrawCategory
            | FxSpotCategory
            | SettlementAccountCategory
            | DeductionRebateCategory,
          ruleId: string,
          column: RuleColumn
        ) => {
          set(state =>
            updateSectionWithValidation(
              state,
              templateId,
              instrumentType,
              sectionId,
              config => ({
                ...config,
                categoryRules: (config.categoryRules || []).map(
                  categorySection =>
                    categorySection.categoryId === categoryId
                      ? {
                        ...categorySection,
                        rules: categorySection.rules.map(rule =>
                          rule.id === ruleId
                            ? updateRuleColumns(rule, columns => [
                              ...columns,
                              column,
                            ])
                            : rule
                        ),
                      }
                      : categorySection
                ),
              })
            )
          );
        },

        removeColumnFromCategoryRule: (
          templateId: string,
          instrumentType: UIInstrumentType,
          sectionId: string,
          categoryId:
            | FeeTypeCategory
            | BuySellCategory
            | DepositWithdrawCategory
            | FxSpotCategory
            | SettlementAccountCategory
            | DeductionRebateCategory,
          ruleId: string,
          columnId: string
        ) => {
          set(state =>
            updateSectionWithValidation(
              state,
              templateId,
              instrumentType,
              sectionId,
              config => ({
                ...config,
                categoryRules: (config.categoryRules || []).map(
                  categorySection =>
                    categorySection.categoryId === categoryId
                      ? {
                        ...categorySection,
                        rules: categorySection.rules.map(rule =>
                          rule.id === ruleId
                            ? updateRuleColumns(rule, columns =>
                              columns.filter(col => col.id !== columnId)
                            )
                            : rule
                        ),
                      }
                      : categorySection
                ),
              })
            )
          );
        },

        updateCategoryRuleColumn: (
          templateId: string,
          instrumentType: UIInstrumentType,
          sectionId: string,
          categoryId:
            | FeeTypeCategory
            | BuySellCategory
            | DepositWithdrawCategory
            | FxSpotCategory
            | SettlementAccountCategory
            | DeductionRebateCategory,
          ruleId: string,
          columnId: string,
          updates: Partial<RuleColumn>
        ) => {
          set(state =>
            updateSectionWithValidation(
              state,
              templateId,
              instrumentType,
              sectionId,
              config => ({
                ...config,
                categoryRules: (config.categoryRules || []).map(
                  categorySection =>
                    categorySection.categoryId === categoryId
                      ? {
                        ...categorySection,
                        rules: categorySection.rules.map(rule =>
                          rule.id === ruleId
                            ? updateRuleColumns(rule, columns =>
                              columns.map(col =>
                                col.id === columnId
                                  ? { ...col, ...updates }
                                  : col
                              )
                            )
                            : rule
                        ),
                      }
                      : categorySection
                ),
              })
            )
          );
        },

        addLabelToCategoryRuleColumn: (
          templateId: string,
          instrumentType: UIInstrumentType,
          sectionId: string,
          categoryId:
            | FeeTypeCategory
            | BuySellCategory
            | DepositWithdrawCategory
            | FxSpotCategory
            | SettlementAccountCategory
            | DeductionRebateCategory,
          ruleId: string,
          columnId: string,
          label: Label
        ) => {
          set(state =>
            updateSectionWithValidation(
              state,
              templateId,
              instrumentType,
              sectionId,
              config => ({
                ...config,
                categoryRules: (config.categoryRules || []).map(
                  categorySection =>
                    categorySection.categoryId === categoryId
                      ? {
                        ...categorySection,
                        rules: categorySection.rules.map(rule =>
                          rule.id === ruleId
                            ? {
                              ...rule,
                              columns: rule.columns.map(col =>
                                col.id === columnId
                                  ? {
                                    ...col,
                                    labels: [...col.labels, label],
                                  }
                                  : col
                              ),
                            }
                            : rule
                        ),
                      }
                      : categorySection
                ),
              })
            )
          );
        },

        removeLabelFromCategoryRuleColumn: (
          templateId: string,
          instrumentType: UIInstrumentType,
          sectionId: string,
          categoryId:
            | FeeTypeCategory
            | BuySellCategory
            | DepositWithdrawCategory
            | FxSpotCategory
            | SettlementAccountCategory
            | DeductionRebateCategory,
          ruleId: string,
          columnId: string,
          labelId: string
        ) => {
          set(state =>
            updateSectionWithValidation(
              state,
              templateId,
              instrumentType,
              sectionId,
              config => ({
                ...config,
                categoryRules: (config.categoryRules || []).map(
                  categorySection =>
                    categorySection.categoryId === categoryId
                      ? {
                        ...categorySection,
                        rules: categorySection.rules.map(rule =>
                          rule.id === ruleId
                            ? {
                              ...rule,
                              columns: rule.columns.map(col =>
                                col.id === columnId
                                  ? {
                                    ...col,
                                    labels: col.labels.filter(label => label.id !== labelId),
                                  }
                                  : col
                              ),
                            }
                            : rule
                        ),
                      }
                      : categorySection
                ),
              })
            )
          );
        },

        // Column Mapping Actions (for sections WITHOUT inline rules)
        addColumnMapping: (
          templateId: string,
          instrumentType: UIInstrumentType,
          sectionId: string,
          mapping: ColumnMapping
        ) => {
          set(state =>
            updateSectionWithValidation(
              state,
              templateId,
              instrumentType,
              sectionId,
              config => ({
                ...config,
                columnMappings: [...(config.columnMappings || []), mapping],
              })
            )
          );
        },

        updateColumnMapping: (
          templateId: string,
          instrumentType: UIInstrumentType,
          sectionId: string,
          columnId: string,
          updates: Partial<ColumnMapping>
        ) => {
          set(state =>
            updateSectionWithValidation(
              state,
              templateId,
              instrumentType,
              sectionId,
              config => ({
                ...config,
                columnMappings: updateInArray(
                  config.columnMappings || [],
                  columnId,
                  updates
                ),
              })
            )
          );
        },

        deleteColumnMapping: (
          templateId: string,
          instrumentType: UIInstrumentType,
          sectionId: string,
          columnId: string
        ) => {
          set(state =>
            updateSectionWithValidation(
              state,
              templateId,
              instrumentType,
              sectionId,
              config => ({
                ...config,
                columnMappings: deleteFromArray(
                  config.columnMappings || [],
                  columnId
                ),
              })
            )
          );
        },

        // Section Config Actions
        updateSectionDateFormat: (
          templateId: string,
          instrumentType: UIInstrumentType,
          sectionId: string,
          dateFormat: string
        ) => {
          set(state =>
            updateSectionWithValidation(
              state,
              templateId,
              instrumentType,
              sectionId,
              config => ({
                ...config,
                dateFormat,
              })
            )
          );
        },

        updateMathOperation: (
          templateId: string,
          instrumentType: UIInstrumentType,
          sectionId: string,
          mathOperation: {
            formula: string;
            variables: { [key: string]: string };
          }
        ) => {
          set(state =>
            updateSectionWithValidation(
              state,
              templateId,
              instrumentType,
              sectionId,
              config => ({
                ...config,
                mathOperation,
              })
            )
          );
        },

        updateCurrencyMappings: (
          templateId: string,
          instrumentType: UIInstrumentType,
          sectionId: string,
          mappings: { currency: Currency; variations: string[] }[]
        ) => {
          set(state =>
            updateSectionWithValidation(
              state,
              templateId,
              instrumentType,
              sectionId,
              config => ({
                ...config,
                currencyMappings: mappings,
              })
            )
          );
        },

        addCurrencyVariation: (
          templateId: string,
          instrumentType: UIInstrumentType,
          sectionId: string,
          currency: Currency,
          variation: string
        ) => {
          set(state =>
            updateSectionWithValidation(
              state,
              templateId,
              instrumentType,
              sectionId,
              config => ({
                ...config,
                currencyMappings: (config.currencyMappings || []).map(m =>
                  m.currency === currency
                    ? {
                      ...m,
                      variations: [...m.variations, variation],
                    }
                    : m
                ),
              })
            )
          );
        },

        removeCurrencyVariation: (
          templateId: string,
          instrumentType: UIInstrumentType,
          sectionId: string,
          currency: Currency,
          variation: string
        ) => {
          set(state =>
            updateSectionWithValidation(
              state,
              templateId,
              instrumentType,
              sectionId,
              config => ({
                ...config,
                currencyMappings: (config.currencyMappings || []).map(m =>
                  m.currency === currency
                    ? {
                      ...m,
                      variations: m.variations.filter(v => v !== variation),
                    }
                    : m
                ),
              })
            )
          );
        },

        updateSignDetection: (
          templateId: string,
          instrumentType: UIInstrumentType,
          sectionId: string,
          signDetection: SignDetectionConfig
        ) => {
          set(state =>
            updateSectionWithValidation(
              state,
              templateId,
              instrumentType,
              sectionId,
              config => ({
                ...config,
                signDetection,
              })
            )
          );
        },

        // Auto-save Actions
        setSaving: (isSaving: boolean) => {
          set({ isSaving });
        },

        setLastSavedAt: (timestamp: string | null) => {
          set({ lastSavedAt: timestamp });
        },

        // COMMENTED OUT: Holdings lookup functionality disabled
        // // Holdings Lookup Actions
        // updateHoldingsLookup: (
        //   templateId: string,
        //   instrumentType: UIInstrumentType,
        //   sectionId: string,
        //   config: { enabled: boolean; symbolColumn: string | null }
        // ) => {
        //   set((state) => updateSectionWithValidation(
        //     state,
        //     templateId,
        //     instrumentType,
        //     sectionId,
        //     (sectionConfig) => ({
        //       ...sectionConfig,
        //       holdingsLookup: config
        //     })
        //   ));
        // },

        // Transfer Labels Actions
        addTransferLabel: (
          templateId: string,
          instrumentType: UIInstrumentType,
          sectionId: string,
          label: Label
        ) => {
          set(state =>
            updateSectionWithValidation(
              state,
              templateId,
              instrumentType,
              sectionId,
              config => {
                // Check for duplicates using CURRENT state
                const isDuplicate = (config.labels || []).some(
                  l => l.value.toLowerCase() === label.value.toLowerCase()
                );

                if (isDuplicate) {
                  // Don't add duplicate - return config unchanged
                  return config;
                }

                // Add the label
                return {
                  ...config,
                  labels: [...(config.labels || []), label],
                };
              }
            )
          );
        },

        removeTransferLabel: (
          templateId: string,
          instrumentType: UIInstrumentType,
          sectionId: string,
          labelId: string
        ) => {
          set(state =>
            updateSectionWithValidation(
              state,
              templateId,
              instrumentType,
              sectionId,
              config => ({
                ...config,
                labels: (config.labels || []).filter(l => l.id !== labelId),
              })
            )
          );
        },

        // Validation Actions
        setValidation: (sectionId: string, result: ValidationResult) => {
          set(state => ({
            validationState: {
              ...state.validationState,
              [sectionId]: result,
            },
          }));
        },

        clearValidation: (sectionId: string) => {
          set(state => {
            const newValidationState = { ...state.validationState };
            delete newValidationState[sectionId];
            return { validationState: newValidationState };
          });
        },

        getValidationForSection: (
          sectionId: string
        ): ValidationResult | null => {
          const state = get();
          const { activeTemplateId, activeInstrumentType, validationState } =
            state;

          if (!activeTemplateId || !activeInstrumentType) {
            return null;
          }

          const key = getValidationKey(
            activeTemplateId,
            activeInstrumentType,
            sectionId
          );
          return validationState[key] || null;
        },

        getTabErrorCount: (instrumentType: UIInstrumentType): number => {
          const state = get();
          const { activeTemplateId, templates, validationState } = state;

          if (!activeTemplateId) return 0;

          const template = templates.find(t => t.id === activeTemplateId);
          if (!template) return 0;

          const instrumentConfig = template.instrumentConfigs.find(
            ic => ic.instrumentType === instrumentType
          );
          if (!instrumentConfig) return 0;

          // Count errors across all sections in this tab
          let errorCount = 0;
          instrumentConfig.sections.forEach(section => {
            const key = getValidationKey(
              activeTemplateId,
              instrumentType,
              section.id
            );
            const validation = validationState[key];
            if (validation && !validation.isValid) {
              errorCount++;
            }
          });

          return errorCount;
        },
      };
    },
    {
      name: 'configuration-store',
    }
  )
);

// Custom hook wrapper
export const useConfiguration = () => {
  const store = useConfigurationStore();
  return store;
};

export default useConfigurationStore;
